import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import TaskStageBadge from "../components/TaskStageBadge";
import { isTaskDone } from "../lib/taskStage";
import WorksectionTaskSection from "../components/WorksectionTaskSection";
import api, { tasksApi, commentsApi, taskPaymentsApi, getFileUrl, timelineApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import CreateClientRequestDialog from "../components/CreateClientRequestDialog";
import DynamicChecklist from "../components/DynamicChecklist";
import SetupChatButton from "../components/SetupChatButton";
import TaskStageHistory from "../components/TaskStageHistory";
import StaffHoursTracker from "../components/StaffHoursTracker";
import TaskTimeline from "../components/TaskTimeline";
import { DeadlinePhasesCurrent, DeadlinePhasesTimeline } from "../components/DeadlinePhases";
import {
  ArrowLeft,
  Upload,
  FileImage,
  Calendar,
  User,
  DollarSign,
  Check,
  X,
  MessageSquare,
  Send,
  Download,
  Eye,
  EyeOff,
  Clock,
  Trash2,
  RotateCcw,
  Plus,
  CheckCircle2,
  SplitSquareHorizontal,
  AlertTriangle,
  Link2,
  ExternalLink,
  XCircle,
  CheckCircle,
  AlertCircle,
  Pencil,
  Brain,
  Paperclip,
  Sparkles,
  ArrowRight,
  Undo2,
} from "lucide-react";

const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatAmount: formatAmountRaw } = useCurrency();
  const fileInputRef = useRef(null);
  const versionInputRef = useRef(null);
  const commentInputRef = useRef(null);

  // Format amount with task's currency (default to UAH if not specified)
  const formatAmount = (amount, currency = null) => {
    const curr = currency || task?.currency || "UAH";
    return formatAmountRaw(amount, curr);
  };

  const [task, setTask] = useState(null);
  // Setup chat now lives on the task. Keywords decide whether this task type has
  // a setup flow at all; setupGate says whether the manager still owes fields
  // before the task can start.
  const [setupChatKeywords, setSetupChatKeywords] = useState([]);
  const [setupGate, setSetupGate] = useState(null);
  const refreshSetupGate = useCallback(() => {
    api.get(`/tasks/${id}/setup-gate`).then(({ data }) => setSetupGate(data)).catch(() => setSetupGate(null));
  }, [id]);
  // Mirrors STARTED_TASK_STATUSES on the backend: once a task has an artist or
  // has left "new", the gate no longer applies to it at all.
  const STARTED_TASK_STATUSES = ["assigned", "in_progress", "review", "revision", "approved", "completed"];
  const [comments, setComments] = useState([]);
  const [taskPayments, setTaskPayments] = useState([]);
  const [clientRequests, setClientRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [versionComment, setVersionComment] = useState("");
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionComment, setRevisionComment] = useState("");
  const [revisionLink, setRevisionLink] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [deadlineValue, setDeadlineValue] = useState("");
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [newPaymentAmount, setNewPaymentAmount] = useState("");
  const [isAdvancePayment, setIsAdvancePayment] = useState(false);
  const [overageReason, setOverageReason] = useState("");
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [taskTimeline, setTaskTimeline] = useState(null);
  // A stage the PM clicked on the timeline, waiting for "Move".
  const [pendingStageIndex, setPendingStageIndex] = useState(null);
  const [movingStage, setMovingStage] = useState(false);
  // New state for links
  const [versionUrl, setVersionUrl] = useState("");
  const [referenceUrl, setReferenceUrl] = useState("");
  const [referenceTitle, setReferenceTitle] = useState("");
  const [addReferenceDialogOpen, setAddReferenceDialogOpen] = useState(false);
  // Internal review
  const [internalReviewDialogOpen, setInternalReviewDialogOpen] = useState(false);
  const [reviewingVersion, setReviewingVersion] = useState(null);
  const [internalReviewComment, setInternalReviewComment] = useState("");
  
  // Edit description/brief
  const [editDescriptionOpen, setEditDescriptionOpen] = useState(false);
  const [editDescription, setEditDescription] = useState("");
  const [editBrief, setEditBrief] = useState("");
  
  // Edit name
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  
  // Edit art director
  const [artDirectors, setArtDirectors] = useState([]);
  const [adDialogOpen, setAdDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  // Setup-chat mapping + gate — own effect so a 403 never takes the page down.
  useEffect(() => {
    let cancelled = false;
    api.get("/setup-chat/service-types-with-flow")
      .then(({ data }) => { if (!cancelled) setSetupChatKeywords(data?.keywords || []); })
      .catch(() => { if (!cancelled) setSetupChatKeywords([]); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => { if (id) refreshSetupGate(); }, [id, refreshSetupGate]);

  // The gate bites on ONE action — handing the task to an artist — and only
  // while the task hasn't started. Status changes are never blocked, so the
  // banner must not claim they are (backlog b602bd03).
  const setupGateBlocks = Boolean(
    setupGate && setupGate.gates && !setupGate.ok
    && !task?.assigned_artist_id
    && !STARTED_TASK_STATUSES.includes(task?.status)
  );

  const fetchData = async () => {
    try {
      const [taskRes, commentsRes, paymentsRes, requestsRes] = await Promise.all([
        tasksApi.getOne(id),
        commentsApi.getAll(id),
        taskPaymentsApi.getAll({ task_id: id }).catch(() => ({ data: [] })),
        api.get("/client-requests").catch(() => ({ data: [] })),
      ]);
      setTask(taskRes.data);
      setComments(commentsRes.data);
      setTaskPayments(paymentsRes.data);
      // Filter requests for this task
      const taskRequests = (requestsRes.data || []).filter(r => r.task_id === id);
      setClientRequests(taskRequests);
      
      // Fetch timeline for this task
      try {
        const timelineRes = await timelineApi.getTaskTimeline(id);
        if (timelineRes.data) {
          setTaskTimeline(timelineRes.data);
        }
      } catch (e) {
        setTaskTimeline(null);
      }
      
      // Fetch art directors (role=art_director only)
      try {
        const adRes = await api.get("/all-users?role=art_director&is_active=true");
        setArtDirectors(adRes.data || []);
      } catch (e) {
        setArtDirectors([]);
      }
    } catch (error) {
      console.error("Error fetching task:", error);
      toast.error("Не удалось загрузить задачу");
      navigate("/tasks");
    } finally {
      setLoading(false);
    }
  };

  // Put the task on a stage of its timeline — the next one, a jump or back.
  // Reaching the last stage closes the task, so the task itself is reloaded too.
  const moveToStage = async (stageIndex) => {
    const stage = taskTimeline?.stages?.[stageIndex];
    if (!stage) return;
    setMovingStage(true);
    try {
      await timelineApi.updateTaskStage(id, { stage_id: stage.id });
      toast.success(`Stage: ${stage.name}`);
      setPendingStageIndex(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : "Failed to move the stage");
    } finally {
      setMovingStage(false);
    }
  };

  // Save task name
  const handleSaveName = async () => {
    if (!editName.trim() || editName === task.name) {
      setEditingName(false);
      return;
    }
    try {
      await tasksApi.update(id, { name: editName.trim() });
      setTask((prev) => ({ ...prev, name: editName.trim() }));
      toast.success("Task name updated");
    } catch (error) {
      toast.error("Failed to update name");
    }
    setEditingName(false);
  };

  // Save deadline — editable inline after creation (backlog 73ca0f0c: PMs had
  // to delete & recreate the task just to change the date). Sends ISO date; the
  // backend TaskUpdate.deadline accepts it. Empty clears the deadline.
  const handleToggleClientVisibility = async () => {
    const next = task.visible_to_client === false;  // currently internal → make it visible
    try {
      await tasksApi.update(id, { visible_to_client: next });
      toast.success(next ? "Task is now visible to the client" : "Task is now internal");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change visibility");
    }
  };

  const handleSaveDeadline = async () => {
    try {
      await tasksApi.update(id, { deadline: deadlineValue || null });
      setTask((prev) => ({ ...prev, deadline: deadlineValue || null }));
      toast.success(deadlineValue ? "Deadline updated" : "Deadline cleared");
    } catch (error) {
      toast.error("Failed to update deadline");
    }
    setEditingDeadline(false);
  };

  // Save art director
  const handleSetArtDirector = async (adId) => {
    try {
      const ad = artDirectors.find((a) => a.id === adId);
      await tasksApi.update(id, {
        art_director_id: adId || null,
        art_director_review: !!adId,
      });
      setTask((prev) => ({
        ...prev,
        art_director_id: adId || null,
        art_director_name: ad?.name || null,
        art_director_review: !!adId,
      }));
      toast.success(adId ? `Art Director: ${ad?.name}` : "Art Director removed");
      setAdDialogOpen(false);
    } catch (error) {
      toast.error("Failed to update art director");
    }
  };

  // Add reference link (new)
  const handleAddReferenceLink = async () => {
    if (!referenceUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    try {
      await tasksApi.addReferenceLink(id, {
        url: referenceUrl,
        title: referenceTitle || null,
      });
      toast.success("Reference link added");
      setAddReferenceDialogOpen(false);
      setReferenceUrl("");
      setReferenceTitle("");
      fetchData();
    } catch (error) {
      toast.error("Failed to add reference link");
    }
  };

  // Delete reference
  const handleDeleteReference = async (referenceId) => {
    try {
      await tasksApi.deleteReference(id, referenceId);
      toast.success("Reference deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete reference");
    }
  };

  // Legacy file upload (kept for backward compatibility)
  const handleReferenceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await tasksApi.uploadReference(id, file);
      toast.success("Reference uploaded");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload reference");
    } finally {
      setUploading(false);
    }
  };

  // Submit version link (new - artist submits URL)
  const handleSubmitVersionLink = async () => {
    if (!versionUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    try {
      await tasksApi.submitVersionLink(id, {
        url: versionUrl,
        comment: versionComment || null,
      });
      toast.success(canAttachResult ? "Result link attached" : "Version submitted for internal review");
      setVersionDialogOpen(false);
      setVersionUrl("");
      setVersionComment("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit version");
    }
  };

  // Legacy file upload version
  const handleVersionUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await tasksApi.uploadVersion(id, file, versionComment);
      toast.success("Version uploaded");
      setVersionDialogOpen(false);
      setVersionComment("");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload version");
    } finally {
      setUploading(false);
    }
  };

  // Internal review handlers (Art Director / PM Lead / PM)
  const handleInternalApprove = async (versionId) => {
    try {
      await tasksApi.internalReviewVersion(id, versionId, {
        action: "approve",
        comment: internalReviewComment || null,
      });
      toast.success("Version approved for client");
      setInternalReviewDialogOpen(false);
      setReviewingVersion(null);
      setInternalReviewComment("");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve version");
    }
  };

  const handleInternalReject = async (versionId) => {
    if (!internalReviewComment.trim()) {
      toast.error("Please provide feedback for the artist");
      return;
    }
    try {
      await tasksApi.internalReviewVersion(id, versionId, {
        action: "reject",
        comment: internalReviewComment,
      });
      toast.success("Version returned to artist for revision");
      setInternalReviewDialogOpen(false);
      setReviewingVersion(null);
      setInternalReviewComment("");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject version");
    }
  };

  // Legacy approve (for backward compatibility)
  const handleApproveVersion = async (versionId) => {
    try {
      await tasksApi.approveVersion(id, versionId);
      toast.success("Version approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve version");
    }
  };

  // Client revision request (new)
  const handleClientRevisionRequest = async () => {
    if (!revisionComment.trim()) {
      toast.error("Please provide revision feedback");
      return;
    }
    try {
      const response = await tasksApi.requestClientRevision(id, {
        comment: revisionComment,
        link: revisionLink || null,
      });
      const { is_paid, free_remaining } = response.data;
      if (is_paid) {
        toast.warning(`Paid revision requested. This revision will be charged.`);
      } else {
        toast.success(`Revision requested. ${free_remaining} free revisions remaining.`);
      }
      setRevisionDialogOpen(false);
      setRevisionComment("");
      setRevisionLink("");
      fetchData();
    } catch (error) {
      toast.error("Failed to request revision");
    }
  };

  // Legacy revision request
  const handleRequestRevision = async () => {
    if (!revisionComment.trim()) {
      toast.error("Please provide revision feedback");
      return;
    }
    try {
      await tasksApi.requestRevision(id, revisionComment);
      toast.success("Revision requested");
      setRevisionDialogOpen(false);
      setRevisionComment("");
      fetchData();
    } catch (error) {
      toast.error("Failed to request revision");
    }
  };

  const handleDeleteTask = async () => {
    try {
      await tasksApi.delete(id);
      toast.success("Task deleted");
      navigate(`/projects/${task.project_id}`);
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  // Save description and brief
  const handleSaveDescription = async () => {
    try {
      await tasksApi.update(id, { 
        description: editDescription,
        brief: editBrief 
      });
      toast.success("Description updated");
      setEditDescriptionOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update description");
    }
  };

  // Open edit description dialog
  const openEditDescription = () => {
    setEditDescription(task.description || "");
    setEditBrief(task.brief || "");
    setEditDescriptionOpen(true);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await commentsApi.create({ task_id: id, content: newComment });
      setNewComment("");
      fetchData();
    } catch (error) {
      toast.error("Не удалось добавить комментарий");
    }
  };

  const handleAddPayment = async () => {
    const amount = parseFloat(newPaymentAmount);
    if (!amount || amount <= 0) {
      toast.error("Введите корректную сумму");
      return;
    }
    
    // Check if this is an overage payment
    const taskBudget = task?.agreed_price || task?.client_price || 0;
    const existingTotal = taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0);
    const isOverage = (existingTotal + amount) > taskBudget && taskBudget > 0;
    
    if (isOverage && !overageReason.trim()) {
      toast.error("Сумма превышает бюджет задачи. Укажите причину превышения.");
      return;
    }
    
    try {
      await taskPaymentsApi.create({
        task_id: id,
        amount: amount,
        is_advance: isAdvancePayment,
        overage_reason: isOverage ? overageReason : null,
        notes: isAdvancePayment ? "Аванс 50%" : "Оплата за задачу",
      });
      toast.success("Платёж добавлен");
      setPaymentDialogOpen(false);
      setNewPaymentAmount("");
      setIsAdvancePayment(false);
      setOverageReason("");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Не удалось создать платёж";
      toast.error(detail);
    }
  };
  
  const fetchPaymentSummary = async () => {
    try {
      const res = await api.get(`/tasks/${id}/payment-summary`);
      setPaymentSummary(res.data);
    } catch (error) {
      console.error("Error fetching payment summary:", error);
    }
  };
  
  const openPaymentDialog = () => {
    fetchPaymentSummary();
    setPaymentDialogOpen(true);
  };

  const handleConfirmPayment = async (paymentId) => {
    try {
      await taskPaymentsApi.update(paymentId, "confirmed");
      toast.success("Платёж подтверждён");
      fetchData();
    } catch (error) {
      toast.error("Ошибка подтверждения");
    }
  };

  const handleApprovePayment = async (paymentId) => {
    try {
      await taskPaymentsApi.update(paymentId, "approved");
      toast.success("Платёж утверждён");
      fetchData();
    } catch (error) {
      toast.error("Ошибка утверждения");
    }
  };

  const handleMarkPaymentPaid = async (paymentId) => {
    try {
      await taskPaymentsApi.update(paymentId, "paid");
      toast.success("Платёж выполнен");
      fetchData();
    } catch (error) {
      toast.error("Ошибка оплаты");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment request? This cannot be undone.")) return;
    try {
      await taskPaymentsApi.delete(paymentId);
      toast.success("Payment request deleted");
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail || "Failed to delete payment";
      toast.error(detail);
    }
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending: "payment-pending",
      approved: "payment-approved",
      paid: "payment-paid",
    };
    return (
      <Badge variant="outline" className={cn("capitalize", styles[status])}>
        {status}
      </Badge>
    );
  };

  const isImageFile = (filename) => {
    return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(filename);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  if (!task) return null;

  const canEdit = user?.role === "admin" || user?.role === "team" || user?.role === "pm_lead" || user?.role === "pm";
  // Mirrors backend allowlist on DELETE /task-payments/{id}.
  const canDeletePayment = ["admin", "pm_lead", "crm_manager", "finance"].includes(user?.role);
  // Mirrors backend allowlist on PUT /task-payments/{id}: confirm/approve is PM-level work,
  // marking money as actually paid is finance/admin only — same rule as on /payments.
  const canApprovePayment = ["admin", "team", "pm", "pm_lead", "crm_manager", "finance"].includes(user?.role);
  const canMarkPaymentPaid = ["admin", "finance"].includes(user?.role);
  // Mirrors backend allowlist on POST /task-payments — finance sees the block to pay,
  // but creating a payment request is not theirs, so no dead "Add" button for them.
  const canCreatePayment = ["admin", "team", "pm", "pm_lead", "crm_manager"].includes(user?.role);
  const isArtDirector = user?.role === "art_director";
  // Whoever the task was handed to can submit for it — the gate is "are you the
  // executor", not "is your portal role artist". An art director who also takes
  // tasks saw no Submit button at all and asked how to attach the link.
  // backlog 47f89bf7.
  const isAssignedExecutor = task.assigned_artist_id && task.assigned_artist_id === user?.id;
  const canUploadVersion = isAssignedExecutor &&
    ["in_progress", "revision", "review"].includes(task.status);
  // Closed work with the deliverable missing: the executor may still file the
  // link as a record. It does not reopen the task (backend enforces that too).
  const canAttachResult = isAssignedExecutor && ["completed", "approved"].includes(task.status);
  // Partners and clients can approve/request revision on internally approved versions
  const canClientApprove = user?.role === "client" || user?.role === "partner";
  // The client cabinet shows the task's stages only — not our internal phases,
  // statuses or who moved what. Partners (TRP) keep the full view.
  const isClientRole = user?.role === "client";
  // PM can approve on behalf of client if client doesn't respond
  const canApproveForClient = user?.role === "admin" || user?.role === "pm" || user?.role === "pm_lead";

  return (
    <div data-testid="task-detail-page">
      {/* Back Button */}
      <Link
        to={user?.role === "partner" ? `/trp/project/${task.project_id}` : `/unified-projects/${task.project_id}`}
        className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-50 transition-colors mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to {task.project_name}</span>
      </Link>

      {/* Awaiting client approval banner for admin/PM */}
      {canApproveForClient && task.status === "awaiting_client" && (() => {
        const pendingVersion = (task.versions || []).find(v => v.internal_status === "approved" && !v.approved && !v.client_approved);
        if (!pendingVersion) return null;
        return (
          <div className="mb-6 p-4 rounded-lg border border-orange-500/40 bg-orange-950/30 flex items-center justify-between" data-testid="client-approval-banner">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-orange-400" />
              <div>
                <p className="text-orange-200 font-medium text-sm">Awaiting client approval</p>
                <p className="text-orange-400/70 text-xs">Version {pendingVersion.version_number || "—"} sent to client</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleApproveVersion(pendingVersion.id)}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="banner-approve-for-client"
              >
                <Check className="h-4 w-4 mr-1" />
                Approve (for client)
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setReviewingVersion(pendingVersion);
                  setRevisionDialogOpen(true);
                }}
                className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Revision
              </Button>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-8">
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            {editingName && canEdit ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 text-2xl font-bold h-10 w-[400px]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") setEditingName(false);
                  }}
                  data-testid="edit-task-name-input"
                />
                <Button size="sm" onClick={handleSaveName} className="bg-green-600 hover:bg-green-700 h-8" data-testid="save-task-name-btn">
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingName(false)} className="h-8 text-zinc-400">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <h1
                className={cn("text-3xl font-bold text-zinc-50 font-['Manrope']", canEdit && "cursor-pointer hover:text-zinc-300 transition-colors")}
                onClick={() => {
                  if (canEdit) {
                    setEditName(task.name);
                    setEditingName(true);
                  }
                }}
                title={canEdit ? "Click to edit" : undefined}
                data-testid="task-name"
              >
                {task.name}
              </h1>
            )}
            <TaskStageBadge task={task} size="md" />
          </div>
          <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 capitalize mb-4">
            {task.task_type.replace("_", " ")}
          </Badge>
          <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500">
            {/* Artist name - hide from clients and partners */}
            {task.assigned_artist_name && user?.role !== "client" && user?.role !== "partner" && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{task.assigned_artist_name}</span>
              </div>
            )}
            {/* Art Director */}
            {user?.role !== "client" && user?.role !== "partner" && (
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-purple-400" />
                {task.art_director_name ? (
                  <span className="text-purple-400">{task.art_director_name}</span>
                ) : (
                  <span className="text-zinc-600 italic">No AD</span>
                )}
                {canEdit && (
                  <button
                    onClick={() => setAdDialogOpen(true)}
                    className="text-zinc-600 hover:text-purple-400 transition-colors"
                    title="Change Art Director"
                    data-testid="edit-art-director-btn"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
            {/* Who sees this task. Absent means visible — that is how every
                task behaved before the flag. backlog 46d2b81b. */}
            {canEdit && (
              <button
                type="button"
                onClick={handleToggleClientVisibility}
                className={cn("inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs transition-colors",
                  task.visible_to_client === false
                    ? "border-zinc-700 text-zinc-400 hover:text-zinc-200"
                    : "border-orange-500/40 text-orange-400 hover:border-orange-500")}
                title={task.visible_to_client === false ? "Internal — hidden from the client cabinet" : "Visible in the client cabinet"}
                data-testid="task-client-visibility-toggle"
              >
                {task.visible_to_client === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {task.visible_to_client === false ? "Internal" : "Visible to client"}
              </button>
            )}
            {editingDeadline ? (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <input
                  type="date"
                  value={deadlineValue}
                  autoFocus
                  onChange={(e) => setDeadlineValue(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded px-2 py-0.5 text-sm text-zinc-100"
                />
                <button
                  onClick={handleSaveDeadline}
                  className="text-emerald-400 hover:text-emerald-300"
                  title="Save deadline"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setEditingDeadline(false)}
                  className="text-zinc-500 hover:text-zinc-300"
                  title="Cancel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : task.deadline ? (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Due {new Date(task.deadline).toLocaleDateString()}</span>
                {canEdit && (
                  <button
                    onClick={() => { setDeadlineValue(String(task.deadline).slice(0, 10)); setEditingDeadline(true); }}
                    className="text-zinc-600 hover:text-sky-400 transition-colors"
                    title="Edit deadline"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
            ) : canEdit ? (
              <button
                onClick={() => { setDeadlineValue(""); setEditingDeadline(true); }}
                className="flex items-center gap-2 text-zinc-500 hover:text-sky-400 transition-colors"
                title="Set deadline"
              >
                <Calendar className="h-4 w-4" />
                <span className="italic">Set deadline</span>
              </button>
            ) : null}
            {/* Price - removed from header, shown only in Payments section */}
          </div>
          
          {/* Deadline Phases — internal (Artist Work, review…); a client
              sees only the stages of the task's timeline. WS 23241245. */}
          {!isClientRole && task.deadline_phases && task.deadline_phases.length > 0 && (
            <div className="mt-3">
              <DeadlinePhasesCurrent phases={task.deadline_phases} />
            </div>
          )}
        </div>

        {canEdit && (
          <div className="flex flex-wrap gap-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-red-900 text-red-400 hover:bg-red-900/20" data-testid="delete-task-btn">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-zinc-50">Delete Task?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    This will permanently delete the task and all its versions. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteTask} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-task-btn">
                    Delete Task
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {/* Task Progress — the task's stage on its timeline is the only notion of
          "where is it". The next-stage button covers the usual step; clicking a
          dot jumps or goes back, after a confirmation that says what changes. */}
      {taskTimeline && taskTimeline.stages && taskTimeline.stages.length > 0 && (() => {
        const stages = taskTimeline.stages;
        const current = taskTimeline.current_stage_index || 0;
        const lastIndex = stages.length - 1;
        const next = current < lastIndex ? stages[current + 1] : null;
        const prev = current > 0 ? stages[current - 1] : null;
        const pending = pendingStageIndex !== null ? stages[pendingStageIndex] : null;
        const pendingNote = (() => {
          if (pendingStageIndex === null) return "";
          if (pendingStageIndex === lastIndex) return "The task closes as done.";
          if (current === lastIndex) return "The task reopens.";
          const gap = pendingStageIndex - current;
          if (gap > 1) return `Skips ${gap - 1} stage${gap - 1 > 1 ? "s" : ""}: ${stages.slice(current + 1, pendingStageIndex).map((st) => st.name).join(", ")}.`;
          if (gap < 0) return `Moves back ${-gap} stage${-gap > 1 ? "s" : ""}.`;
          return "";
        })();
        return (
          <Card className="bg-zinc-900/50 border-zinc-800 mb-8" data-testid="task-stage-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3 space-y-0">
              <CardTitle className="text-zinc-50 font-['Manrope'] text-base flex items-center gap-2 min-w-0">
                <Clock className="h-4 w-4 text-orange-500 shrink-0" />
                Task Progress
                {taskTimeline?.template_name && (
                  <span className={cn(
                    "text-[11px] font-normal truncate",
                    taskTimeline?.is_universal ? "text-zinc-500" : "text-zinc-400"
                  )}>
                    · {taskTimeline.is_universal
                        ? "default stages — no timeline set for this service type"
                        : taskTimeline.template_name}
                  </span>
                )}
                <span className="text-[11px] font-normal text-zinc-500 shrink-0">
                  · stage {current + 1} of {stages.length}
                </span>
              </CardTitle>
              {canEdit && (
                <div className="flex items-center gap-2 shrink-0">
                  {prev && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={movingStage}
                      onClick={() => moveToStage(current - 1)}
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8"
                      title={`Back to ${prev.name}`}
                      data-testid="stage-back-btn"
                    >
                      <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                      Back
                    </Button>
                  )}
                  {next ? (
                    <Button
                      size="sm"
                      disabled={movingStage}
                      // Into the last stage closes the task — that one asks first.
                      onClick={() => (current + 1 === lastIndex ? setPendingStageIndex(lastIndex) : moveToStage(current + 1))}
                      className="bg-orange-500 hover:bg-orange-600 text-white h-8 max-w-[260px]"
                      data-testid="stage-next-btn"
                    >
                      <span className="truncate">{next.name}</span>
                      <ArrowRight className="h-3.5 w-3.5 ml-1.5 shrink-0" />
                    </Button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
                      <CheckCircle2 className="h-4 w-4" />
                      Done
                    </span>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              <TaskTimeline
                stages={stages}
                currentStageIndex={current}
                showLabels={true}
                size="default"
                interactive={canEdit}
                pendingIndex={pendingStageIndex}
                onStageClick={canEdit ? (_stageId, index) => setPendingStageIndex(index) : undefined}
              />
              {pending && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2.5" data-testid="stage-confirm">
                  <p className="text-sm text-zinc-200">
                    Move to <span className="font-medium text-zinc-50">{pending.name}</span>?
                    {pendingNote && <span className="text-zinc-400"> {pendingNote}</span>}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-zinc-700 h-8" onClick={() => setPendingStageIndex(null)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      disabled={movingStage}
                      className="bg-orange-500 hover:bg-orange-600 text-white h-8"
                      onClick={() => moveToStage(pendingStageIndex)}
                      data-testid="stage-confirm-btn"
                    >
                      Move
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description & Brief */}
          {(task.description || task.brief || canEdit) && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-zinc-50 font-['Manrope']">Brief</CardTitle>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-zinc-400 hover:text-zinc-50"
                    onClick={openEditDescription}
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {task.description ? (
                  <div>
                    <p className="text-sm text-zinc-500 mb-1">Description</p>
                    <p className="text-zinc-300">{task.description}</p>
                  </div>
                ) : canEdit && (
                  <p className="text-sm text-zinc-500 italic">No description. Click Edit to add one.</p>
                )}
                {task.brief && (
                  <div>
                    <p className="text-sm text-zinc-500 mb-1">Detailed Brief</p>
                    <p className="text-zinc-300 whitespace-pre-wrap">{task.brief}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Internal Notes (Team only) */}
          {task.internal_notes && canEdit && (
            <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-purple-500">
              <CardHeader>
                <CardTitle className="text-zinc-50 font-['Manrope'] text-sm flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  Internal Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-zinc-400 text-sm">{task.internal_notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Deadline Phases Timeline */}
          {!isClientRole && task.deadline_phases && task.deadline_phases.length > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-zinc-50 font-['Manrope'] text-sm flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-400" />
                  Deadline Phases
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DeadlinePhasesTimeline phases={task.deadline_phases} />
              </CardContent>
            </Card>
          )}

          {/* WORKSECTION SECTION — internal roles only (no clients/partners).
              Shows aggregated hours+cost from linked WS tasks (Phase 3 preview,
              no write to task.staff_cost_total). Component is collapsed by
              default — header carries linked/not-linked + counts. */}
          {user?.role && !["client", "partner"].includes(user.role) && (
            <WorksectionTaskSection
              task={task}
              canEdit={["admin", "pm_lead", "pm", "team"].includes(user.role)}
            />
          )}

          {/* Versions */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-zinc-50 font-['Manrope']">
                  {/* Show correct version count for partners/clients - only approved versions */}
                  {(user?.role === "client" || user?.role === "partner") ? (
                    <>Versions ({(task.versions || []).filter(v => v.internal_status === "approved").length})</>
                  ) : (
                    <>Versions ({task.versions?.length || 0})</>
                  )}
                </CardTitle>
                {/* Version counter - visible to all */}
                {(() => {
                  const clientVersions = (task.versions || []).filter(v => v.internal_status === "approved").length;
                  const freeLimit = task.free_revisions_limit || 3;
                  const freeRemaining = Math.max(0, freeLimit - clientVersions);
                  
                  // Only show if there are versions or if near limit
                  if (clientVersions === 0 && freeLimit === 3) return null;
                  
                  return (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-zinc-500">Client versions:</span>
                      {clientVersions < freeLimit ? (
                        <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                          {freeRemaining}/{freeLimit} free remaining
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/30">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Free versions used. Next will be paid.
                        </Badge>
                      )}
                      {(task.paid_revisions || 0) > 0 && (
                        <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30">
                          {task.paid_revisions} paid
                        </Badge>
                      )}
                    </div>
                  );
                })()}
              </div>
              {(canUploadVersion || canAttachResult) && (
                <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className={canAttachResult ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700" : "bg-orange-500 hover:bg-orange-600 text-white"} data-testid="upload-version-btn">
                      <Link2 className="h-4 w-4 mr-2" />
                      {canAttachResult ? "Attach result link" : "Submit Work"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="text-zinc-50">{canAttachResult ? "Attach result link" : "Submit New Version"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Work URL *</Label>
                        <Input
                          value={versionUrl}
                          onChange={(e) => setVersionUrl(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-zinc-50"
                          placeholder="https://drive.google.com/... or Dropbox link"
                          data-testid="version-url-input"
                        />
                        <p className="text-xs text-zinc-500">Link to your work (Google Drive, Dropbox, etc.)</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Comment (Optional)</Label>
                        <Textarea
                          value={versionComment}
                          onChange={(e) => setVersionComment(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-zinc-50"
                          placeholder="Describe what changed in this version..."
                          data-testid="version-comment-input"
                        />
                      </div>
                      <Button
                        onClick={handleSubmitVersionLink}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                        disabled={!versionUrl.trim()}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {canAttachResult ? "Attach link" : "Submit for Review"}
                      </Button>
                      <p className="text-xs text-zinc-500 text-center">
                        {canAttachResult
                          ? "The task is already closed — the link is filed as a record and nothing is reopened"
                          : "Your work will be reviewed internally before being shown to the client"}
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {task.versions?.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <FileImage className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No versions submitted yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {task.versions?.slice().reverse().map((version, idx) => {
                    // For clients and partners: only show internally approved versions
                    const isClientView = user?.role === "client" || user?.role === "partner";
                    const isInternallyApproved = version.internal_status === "approved";
                    if (isClientView && !isInternallyApproved) return null;
                    
                    return (
                      <div
                        key={version.id}
                        className={cn(
                          "p-4 rounded-lg border",
                          version.approved || version.client_approved
                            ? "bg-green-500/5 border-green-500/20"
                            : version.internal_status === "rejected"
                            ? "bg-red-500/5 border-red-500/20"
                            : version.internal_status === "pending_review"
                            ? "bg-yellow-500/5 border-yellow-500/20"
                            : "bg-zinc-800/50 border-zinc-700"
                        )}
                        data-testid={`version-${version.version_number}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="text-xs font-mono text-zinc-500 uppercase">
                                V{version.version_number}
                              </span>
                              {/* Internal status badges (visible to team/artist only - NOT clients/partners) */}
                              {!isClientView && version.internal_status === "pending_review" && (
                                <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending Internal Review
                                </Badge>
                              )}
                              {!isClientView && version.internal_status === "approved" && (
                                <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Sent to Client
                                </Badge>
                              )}
                              {!isClientView && version.internal_status === "rejected" && (
                                <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Needs Revision
                                </Badge>
                              )}
                              {/* Final approval badge */}
                              {(version.approved || version.client_approved) && (
                                <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                                  <Check className="h-3 w-3 mr-1" />
                                  Client Approved
                                </Badge>
                              )}
                            </div>
                            
                            {/* URL link or filename */}
                            {version.type === "link" || version.url ? (
                              <a 
                                href={version.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-orange-400 hover:text-orange-300 font-medium flex items-center gap-1"
                              >
                                <ExternalLink className="h-4 w-4" />
                                View Work
                              </a>
                            ) : (
                              <p className="text-zinc-50 font-medium">{version.filename}</p>
                            )}
                            
                            {version.comment && (
                              <p className="text-sm text-zinc-400 mt-1">{version.comment}</p>
                            )}
                            
                            {/* Internal review comment (if rejected) */}
                            {version.internal_review_comment && version.internal_status === "rejected" && (
                              <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/20">
                                <p className="text-xs text-red-400">
                                  <strong>Feedback:</strong> {version.internal_review_comment}
                                </p>
                              </div>
                            )}

                            {/* AI Quality Control verdict — visible to everyone except clients/partners */}
                            {!isClientView && version.ai_qc_status && version.ai_qc_status !== "skipped" && (
                              <div className={`mt-2 p-2.5 rounded border ${
                                version.ai_qc_status === "passed" ? "bg-green-500/5 border-green-500/20" :
                                version.ai_qc_status === "failed" ? "bg-red-500/5 border-red-500/20" :
                                version.ai_qc_status === "error" ? "bg-amber-500/5 border-amber-500/20" :
                                "bg-blue-500/5 border-blue-500/20"
                              }`}>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-medium">
                                    <Brain className={`h-3.5 w-3.5 ${
                                      version.ai_qc_status === "passed" ? "text-green-400" :
                                      version.ai_qc_status === "failed" ? "text-red-400" :
                                      version.ai_qc_status === "error" ? "text-amber-400" :
                                      "text-blue-400"
                                    }`} />
                                    <span className={
                                      version.ai_qc_status === "passed" ? "text-green-400" :
                                      version.ai_qc_status === "failed" ? "text-red-400" :
                                      version.ai_qc_status === "error" ? "text-amber-400" :
                                      "text-blue-400"
                                    }>
                                      AI QC: {version.ai_qc_status === "passed" ? "pass" :
                                              version.ai_qc_status === "failed" ? "fail" :
                                              version.ai_qc_status === "error" ? "error" :
                                              version.ai_qc_status}
                                    </span>
                                    {typeof version.ai_qc_verdict?.overall_confidence === "number" && (
                                      <span className="text-zinc-500">· conf {Math.round(version.ai_qc_verdict.overall_confidence * 100)}%</span>
                                    )}
                                    {version.ai_qc_override_status === "unblocked" && (
                                      <span className="text-amber-400 text-[10px] px-1 py-0.5 bg-amber-500/10 rounded">overridden by AD</span>
                                    )}
                                  </div>
                                </div>
                                {version.ai_qc_verdict?.overall_summary && (
                                  <p className="text-xs text-zinc-300 mb-1.5">{version.ai_qc_verdict.overall_summary}</p>
                                )}
                                {Array.isArray(version.ai_qc_verdict?.criteria) && version.ai_qc_verdict.criteria.length > 0 && (
                                  <div className="space-y-1 mb-1.5">
                                    {version.ai_qc_verdict.criteria.map((c, i) => (
                                      <div key={i} className="flex items-center gap-2 text-xs">
                                        <span className="font-mono w-36 truncate text-zinc-400">{c.key}</span>
                                        <div className="flex-1 h-1.5 bg-zinc-800 rounded overflow-hidden">
                                          <div
                                            className={`h-full ${c.pass ? "bg-green-500" : "bg-red-500"}`}
                                            style={{ width: `${Math.round((c.score || 0) * 100)}%` }}
                                          />
                                        </div>
                                        <span className="w-8 text-right text-zinc-300">{(c.score || 0).toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {Array.isArray(version.ai_qc_verdict?.issues) && version.ai_qc_verdict.issues.length > 0 && (
                                  <ul className="space-y-0.5 text-xs text-zinc-400 mb-1">
                                    {version.ai_qc_verdict.issues.map((iss, i) => (
                                      <li key={i}>
                                        <span className={`inline-block text-[9px] px-1 py-0.5 rounded mr-1 text-zinc-50 ${
                                          iss.severity === "high" ? "bg-red-600" :
                                          iss.severity === "med" ? "bg-amber-600" :
                                          "bg-zinc-600"
                                        }`}>{iss.severity || "—"}</span>
                                        <b>{iss.area}:</b> {iss.text}
                                        {iss.suggested_fix && <span className="text-zinc-500"> → {iss.suggested_fix}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                <p className="text-[10px] text-zinc-500">
                                  {version.ai_qc_mode === "hard" ? "Автоматическая проверка качества перед арт-директором." :
                                   version.ai_qc_mode === "soft" ? "Подсказка AI — АД решает сам." :
                                   "Фоновая AI-проверка (shadow)."}
                                  {version.ai_qc_error && <> · <span className="text-amber-400">{version.ai_qc_error}</span></>}
                                </p>
                              </div>
                            )}
                            
                            {/* Reviewer info - hidden from clients/partners */}
                            {version.internal_reviewed_by_name && !isClientView && (
                              <p className="text-xs text-zinc-500 mt-1">
                                Reviewed by {version.internal_reviewed_by_name}
                              </p>
                            )}
                            
                            {/* Uploader info - hide artist name for clients/partners */}
                            <p className="text-xs text-zinc-600 mt-2 font-mono">
                              {!isClientView && version.uploaded_by_name && `by ${version.uploaded_by_name} • `}
                              {new Date(version.uploaded_at).toLocaleString()}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* File preview/download (for file type versions) */}
                            {version.type !== "link" && !version.url && (
                              <>
                                {isImageFile(version.filename) && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setPreviewFile(version)}
                                    className="text-zinc-400 hover:text-zinc-50"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                )}
                                <a
                                  href={getFileUrl(version.id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-zinc-400 hover:text-zinc-50 transition-colors"
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              </>
                            )}
                            
                            {/* Internal Review buttons (for team/pm_lead) */}
                            {canEdit && version.internal_status === "pending_review" && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleInternalApprove(version.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setReviewingVersion(version);
                                    setInternalReviewDialogOpen(true);
                                  }}
                                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                            
                            {/* Client/Partner approval buttons - also available to PM/Admin to approve on behalf of client */}
                            {(canClientApprove || canApproveForClient) && version.internal_status === "approved" && !version.approved && !version.client_approved && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApproveVersion(version.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid="client-approve-btn"
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  {canClientApprove ? "Approve" : "Approve (for client)"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setReviewingVersion(version);
                                    setRevisionDialogOpen(true);
                                  }}
                                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                                  data-testid="request-revision-btn"
                                >
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Request Revision
                                </Button>
                              </div>
                            )}
                            
                            {/* Team final approval (legacy) - hide if already showing approve buttons above */}
                            {canEdit && !canClientApprove && !canApproveForClient && version.internal_status === "approved" && !version.approved && !version.client_approved && (
                              <Button
                                size="sm"
                                onClick={() => handleApproveVersion(version.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Final Approve
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Internal Review Rejection Dialog */}
          <Dialog open={internalReviewDialogOpen} onOpenChange={setInternalReviewDialogOpen}>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">Return for Revision</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Feedback for Artist *</Label>
                  <Textarea
                    value={internalReviewComment}
                    onChange={(e) => setInternalReviewComment(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    placeholder="Describe what needs to be changed..."
                    rows={4}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => reviewingVersion && handleInternalReject(reviewingVersion.id)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    disabled={!internalReviewComment.trim()}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Return to Artist
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setInternalReviewDialogOpen(false);
                      setReviewingVersion(null);
                      setInternalReviewComment("");
                    }}
                    className="border-zinc-700 text-zinc-300"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Reference Links */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-zinc-50 font-['Manrope']">
                References ({task.reference_files?.length || 0})
              </CardTitle>
              {canEdit && (
                <Dialog open={addReferenceDialogOpen} onOpenChange={setAddReferenceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      data-testid="add-reference-btn"
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      Add Reference
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="text-zinc-50">Add Reference Link</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">URL *</Label>
                        <Input
                          value={referenceUrl}
                          onChange={(e) => setReferenceUrl(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-zinc-50"
                          placeholder="https://drive.google.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Title (Optional)</Label>
                        <Input
                          value={referenceTitle}
                          onChange={(e) => setReferenceTitle(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-zinc-50"
                          placeholder="Project References, Floor Plans, etc."
                        />
                      </div>
                      <Button
                        onClick={handleAddReferenceLink}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                        disabled={!referenceUrl.trim()}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Reference
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {task.reference_files?.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Link2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No references added</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {task.reference_files?.map((ref) => (
                    <div
                      key={ref.id}
                      className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 group"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {ref.type === "link" || ref.url ? (
                          <ExternalLink className="h-5 w-5 text-orange-400 flex-shrink-0" />
                        ) : (
                          <FileImage className="h-5 w-5 text-zinc-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          {ref.type === "link" || ref.url ? (
                            <a
                              href={ref.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-400 hover:text-orange-300 font-medium truncate block"
                            >
                              {ref.title || ref.url}
                            </a>
                          ) : (
                            <a
                              href={getFileUrl(ref.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-zinc-50 hover:text-orange-400 font-medium truncate block"
                            >
                              {ref.filename}
                            </a>
                          )}
                          {ref.description && (
                            <p className="text-xs text-zinc-500 truncate">{ref.description}</p>
                          )}
                          <p className="text-xs text-zinc-600">
                            {ref.added_by_name && `by ${ref.added_by_name} • `}
                            {new Date(ref.added_at || ref.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteReference(ref.id)}
                          className="text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          {canEdit && task.status === "review" && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-zinc-50 font-['Manrope'] text-lg">Review Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10" 
                  data-testid="request-revision-btn"
                  onClick={() => setRevisionDialogOpen(true)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Request Revision
                </Button>
              </CardContent>
            </Card>
          )}
          
          {/* Client/Partner Revision Dialog - shown outside canEdit block */}
          <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">Request Revision</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Show revision counter */}
                <div className="p-3 bg-zinc-800 rounded-lg">
                  {(task.client_versions_count || 0) < (task.free_revisions_limit || 3) ? (
                    <p className="text-sm text-green-400">
                      <CheckCircle className="h-4 w-4 inline mr-1" />
                      {(task.free_revisions_limit || 3) - (task.client_versions_count || 0)} free revisions remaining
                    </p>
                  ) : (
                    <p className="text-sm text-orange-400">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      This will be a paid revision
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-zinc-300">What needs to be changed?</Label>
                  <Textarea
                    value={revisionComment}
                    onChange={(e) => setRevisionComment(e.target.value)}
                    placeholder="Describe what needs to be revised..."
                    className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-50"
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Reference Link (optional)</Label>
                  <Input
                    value={revisionLink}
                    onChange={(e) => setRevisionLink(e.target.value)}
                    placeholder="https://..."
                    className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setRevisionDialogOpen(false)} className="border-zinc-700">
                    Cancel
                  </Button>
                  <Button onClick={handleClientRevisionRequest} className="bg-orange-500 hover:bg-orange-600" data-testid="submit-revision-btn">
                    Request Revision
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Art Director Selection Dialog */}
          <Dialog open={adDialogOpen} onOpenChange={setAdDialogOpen}>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">Set Art Director</DialogTitle>
              </DialogHeader>
              <div className="space-y-1 mt-2 max-h-[300px] overflow-y-auto">
                <button
                  onClick={() => handleSetArtDirector(null)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors",
                    !task.art_director_id ? "bg-purple-500/10 text-purple-400" : "text-zinc-400 hover:bg-zinc-800"
                  )}
                  data-testid="ad-option-none"
                >
                  No Art Director
                </button>
                {artDirectors.map((ad) => (
                  <button
                    key={ad.id}
                    onClick={() => handleSetArtDirector(ad.id)}
                    className={cn(
                      "w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors",
                      task.art_director_id === ad.id ? "bg-purple-500/10 text-purple-400" : "text-zinc-300 hover:bg-zinc-800"
                    )}
                    data-testid={`ad-option-${ad.id}`}
                  >
                    {ad.name}
                  </button>
                ))}
                {artDirectors.length === 0 && (
                  <p className="text-zinc-500 text-sm text-center py-4">No art directors found</p>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Comments */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-50 font-['Manrope'] text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                {comments.length === 0 ? (
                  <p className="text-center text-zinc-500 py-8">Нет комментариев</p>
                ) : (
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex gap-3" data-testid={`comment-${comment.id}`}>
                        <Avatar className="h-8 w-8 bg-zinc-800 flex-shrink-0">
                          <AvatarFallback className="bg-zinc-800 text-zinc-400 text-xs">
                            {comment.user_name?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-zinc-50">{comment.user_name}</span>
                            <Badge variant="outline" className="text-xs bg-zinc-800 text-zinc-400 border-zinc-700 capitalize">
                              {comment.user_role}
                            </Badge>
                          </div>
                          <p className={cn(
                            "text-sm mt-1 whitespace-pre-wrap",
                            (comment.content || "").startsWith("[REVISION") || (comment.content || "").startsWith("[Art Director Revision")
                              ? "text-orange-400"
                              : "text-zinc-400"
                          )}>
                            {comment.content}
                          </p>
                          {comment.attachment && (
                            <a
                              href={comment.attachment}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-1.5 underline-offset-2 hover:underline"
                            >
                              <Paperclip className="h-3 w-3" />
                              View attachment
                            </a>
                          )}
                          <p className="text-xs text-zinc-600 mt-1 font-mono">
                            {new Date(comment.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              <form onSubmit={handleAddComment} className="mt-4 flex gap-2">
                <Input
                  ref={commentInputRef}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Добавить комментарий..."
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 flex-1"
                  data-testid="comment-input"
                />
                <Button type="submit" size="icon" className="bg-orange-500 hover:bg-orange-600" data-testid="send-comment-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Client Requests Section - visible to managers AND partners/clients */}
          {(canEdit || user?.role === "partner" || user?.role === "client") && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-zinc-50 font-['Manrope'] text-lg flex items-center gap-2">
                  <Send className="h-5 w-5 text-orange-500" />
                  {(user?.role === "partner" || user?.role === "client") ? "Requests" : "Client Requests"} ({clientRequests.length})
                </CardTitle>
                {canEdit && task?.project_id && (
                  <CreateClientRequestDialog
                    projectId={task.project_id}
                    taskId={id}
                    onCreated={fetchData}
                    trigger={
                      <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-8">
                        <Plus className="h-4 w-4 mr-1" />
                        Request
                      </Button>
                    }
                  />
                )}
              </CardHeader>
              <CardContent>
                {clientRequests.length === 0 ? (
                  <div className="text-center py-4 text-zinc-500 text-sm">
                    <p>No client requests</p>
                    {!isClientRole && <p className="text-xs mt-1">Request materials or approval from client</p>}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {clientRequests.map((req) => (
                      <div 
                        key={req.id}
                        className={cn(
                          "p-3 rounded-lg border",
                          req.status === "responded"
                            ? "bg-green-500/5 border-green-500/20"
                            : req.is_overdue
                              ? "bg-red-500/5 border-red-500/20"
                              : "bg-orange-500/5 border-orange-500/20"
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm text-zinc-50 font-medium">{req.title}</p>
                            {req.description && (
                              <p className="text-xs text-zinc-400 mt-1">{req.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className={cn("text-xs",
                                req.status === "responded" 
                                  ? "bg-green-500/10 text-green-400 border-green-500/30"
                                  : req.is_overdue
                                    ? "bg-red-500/10 text-red-400 border-red-500/30"
                                    : "bg-orange-500/10 text-orange-400 border-orange-500/30"
                              )}>
                                {req.status === "responded" 
                                  ? req.response_type 
                                  : req.is_overdue 
                                    ? "OVERDUE" 
                                    : "Pending"
                                }
                              </Badge>
                              <span className="text-xs text-zinc-500">
                                {req.request_type?.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {req.is_overdue && req.status !== "responded" && (
                              <AlertTriangle className="h-4 w-4 text-red-400" />
                            )}
                            {/* Respond button for partners/clients on pending requests */}
                            {(user?.role === "partner" || user?.role === "client") && req.status === "pending" && (
                              <Link 
                                to={`/client-requests/${req.id}`}
                                className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs rounded-md"
                              >
                                Respond
                              </Link>
                            )}
                          </div>
                        </div>
                        {req.response_comment && (
                          <p className="text-xs text-zinc-400 mt-2 p-2 bg-zinc-800/50 rounded">
                            Client: &quot;{req.response_comment}&quot;
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Project setup chat — lives on the task now. Shown when the task
              type maps to a setup flow (spinhouse/website/3d-tours). */}
          {(() => {
            const tt = (task?.service_type || task?.task_type || "").toLowerCase();
            const hasSetupFlow = setupChatKeywords.some((kw) => kw && tt.includes(kw));
            if (!hasSetupFlow) return null;
            const blocked = setupGateBlocks;
            return (
              <Card className={"bg-zinc-900/50 border-zinc-800 " + (blocked ? "border-l-4 border-l-amber-500" : "")}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-zinc-50 flex items-center justify-between gap-2 text-base">
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-cyan-400" />
                      Project setup
                    </span>
                    <SetupChatButton
                      taskId={id}
                      serviceName={task?.name}
                      serviceType={task?.service_type || task?.task_type}
                      canEdit={user?.role !== "client"}
                      onSaved={refreshSetupGate}
                    />
                  </CardTitle>
                </CardHeader>
                {blocked && (
                  <CardContent className="pt-0">
                    <div className="text-xs text-amber-300/90">
                      An artist can’t be assigned until the manager fills the setup:{" "}
                      <span className="text-amber-200">
                        {setupGate.missing.map((m) => m.label).join(" · ")}
                      </span>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })()}

          {/* Task Checklist - Service-specific */}
          {task?.service_type && (user?.role === "admin" || user?.role === "pm_lead" || user?.role === "team") && (
            <DynamicChecklist
              stage="task"
              target="internal"
              serviceType={task.service_type}
              entityId={id}
              entityType="task"
              title={`${task.service_type} Checklist`}
              defaultCollapsed={isTaskDone(task)}
              disabled={isTaskDone(task)}
            />
          )}

          {/* AD Fee section - Art Director view */}
          {isArtDirector && (task.art_director_fee > 0 || task.agreed_price > 0) && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-zinc-50 font-['Manrope'] text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-purple-400" />
                  Task Financials
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between p-2.5 bg-zinc-800/50 rounded-lg">
                    <span className="text-zinc-400 text-sm">Artist Cost</span>
                    <span className="text-zinc-300 font-mono text-sm">{formatAmount(task.agreed_price || 0)}</span>
                  </div>
                  <div className="flex justify-between p-2.5 bg-purple-500/5 border border-purple-500/10 rounded-lg">
                    <span className="text-purple-400 text-sm">Your AD Fee</span>
                    <span className="text-purple-400 font-mono text-sm font-medium">{formatAmount(task.art_director_fee || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Task Payments Section — Admin, PM Lead, PM, Team, Finance.
              Finance is here because "mark paid" is theirs: without the block they
              could only pay from /payments. Creating payments stays a PM job. */}
          {(user?.role === "admin" || user?.role === "pm_lead" || user?.role === "pm" || user?.role === "team" || user?.role === "finance") && task.agreed_price > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-zinc-50 font-['Manrope'] text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-orange-500" />
                  Artist Payment
                </CardTitle>
                <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                  {canCreatePayment && (
                    <DialogTrigger asChild>
                      <Button size="sm" onClick={openPaymentDialog} className="bg-orange-500 hover:bg-orange-600 h-8" data-testid="add-payment-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </DialogTrigger>
                  )}
                  <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-zinc-50">Add Payment</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      {/* Payment Summary */}
                      <div className="p-3 bg-zinc-800/50 rounded-lg space-y-2">
                        <div className="flex justify-between">
                          <span className="text-zinc-400 text-sm">Task Budget:</span>
                          <span className="text-zinc-50 font-mono">{formatAmount(task?.agreed_price || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-400 text-sm">Already Requested:</span>
                          <span className="text-orange-400 font-mono">{formatAmount(taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0))}</span>
                        </div>
                        <div className="flex justify-between border-t border-zinc-700 pt-2">
                          <span className="text-zinc-400 text-sm font-medium">Remaining:</span>
                          <span className={cn(
                            "font-mono font-medium",
                            (task?.agreed_price || 0) - taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0) <= 0 
                              ? "text-red-400" 
                              : "text-green-400"
                          )}>
                            {formatAmount(Math.max(0, (task?.agreed_price || 0) - taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0)))}
                          </span>
                        </div>
                      </div>
                      
                      {/* Quick Amount Buttons */}
                      {taskPayments.filter(p => p.payment_type !== "art_director_fee").length === 0 && task?.agreed_price > 0 && (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setNewPaymentAmount((task.agreed_price * 0.5).toString());
                              setIsAdvancePayment(true);
                            }}
                            className="flex-1 border-sky-500/30 text-sky-400 hover:bg-sky-500/10"
                          >
                            50% Advance ({formatAmount(task.agreed_price * 0.5)})
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setNewPaymentAmount(task.agreed_price.toString());
                              setIsAdvancePayment(false);
                            }}
                            className="flex-1 border-green-500/30 text-green-400 hover:bg-green-500/10"
                          >
                            100% Full ({formatAmount(task.agreed_price)})
                          </Button>
                        </div>
                      )}
                      
                      {/* Remaining Amount Button */}
                      {taskPayments.filter(p => p.payment_type !== "art_director_fee").length > 0 && (task?.agreed_price || 0) - taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0) > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            const remaining = (task?.agreed_price || 0) - taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0);
                            setNewPaymentAmount(remaining.toString());
                            setIsAdvancePayment(false);
                          }}
                          className="w-full border-green-500/30 text-green-400 hover:bg-green-500/10"
                        >
                          Final Payment: {formatAmount((task?.agreed_price || 0) - taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0))}
                        </Button>
                      )}
                      
                      {/* Amount Input */}
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Payment Amount</Label>
                        <Input
                          type="number"
                          value={newPaymentAmount}
                          onChange={(e) => setNewPaymentAmount(e.target.value)}
                          placeholder="Enter amount..."
                          className="bg-zinc-950 border-zinc-800 text-zinc-50"
                          data-testid="payment-amount-input"
                        />
                      </div>
                      
                      {/* Overage Warning */}
                      {newPaymentAmount && parseFloat(newPaymentAmount) + taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0) > (task?.agreed_price || 0) && (task?.agreed_price || 0) > 0 && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg space-y-2">
                          <div className="flex items-center gap-2 text-red-400">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="font-medium text-sm">Amount Exceeds Budget!</span>
                          </div>
                          <p className="text-xs text-red-300">
                            Total will be {formatAmount(parseFloat(newPaymentAmount) + taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0))} 
                            vs budget {formatAmount(task?.agreed_price || 0)}
                          </p>
                          <div className="space-y-1">
                            <Label className="text-red-300 text-xs">Reason for overage *</Label>
                            <Input
                              value={overageReason}
                              onChange={(e) => setOverageReason(e.target.value)}
                              placeholder="Explain why additional payment is needed..."
                              className="bg-red-950/50 border-red-500/30 text-zinc-50 text-sm"
                            />
                          </div>
                        </div>
                      )}
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button variant="outline" onClick={() => {
                          setPaymentDialogOpen(false);
                          setNewPaymentAmount("");
                          setOverageReason("");
                          setIsAdvancePayment(false);
                        }} className="border-zinc-700">
                          Cancel
                        </Button>
                        <Button onClick={handleAddPayment} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-add-payment-btn">
                          Add Payment
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="mb-3 p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Agreed:</span>
                    <span className="font-mono text-zinc-50">{formatAmount(task.agreed_price)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-zinc-400">Artist Payments:</span>
                    <span className="font-mono text-orange-400">{formatAmount(taskPayments.filter(p => p.payment_type !== "art_director_fee").reduce((sum, p) => sum + p.amount, 0))}</span>
                  </div>
                  {taskPayments.some(p => p.payment_type === "art_director_fee") && (
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-purple-400">AD Fee:</span>
                      <span className="font-mono text-purple-400">{formatAmount(taskPayments.filter(p => p.payment_type === "art_director_fee").reduce((sum, p) => sum + p.amount, 0))}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-zinc-400">Paid:</span>
                    <span className="font-mono text-green-400">{formatAmount(taskPayments.filter(p => p.status === "paid").reduce((sum, p) => sum + p.amount, 0))}</span>
                  </div>
                </div>
                
                {taskPayments.length === 0 ? (
                  <p className="text-center text-zinc-500 text-sm py-4">No payments</p>
                ) : (
                  <div className="space-y-2">
                    {taskPayments.map((payment, idx) => (
                      <div 
                        key={payment.id} 
                        className={cn(
                          "p-2 rounded-lg border",
                          payment.is_overage 
                            ? "bg-red-500/10 border-red-500/30" 
                            : payment.status === "paid" 
                              ? "bg-green-500/5 border-green-500/20" 
                              : "bg-zinc-800/50 border-zinc-700"
                        )}
                        data-testid={`task-payment-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-zinc-50">{formatAmount(payment.amount, payment.currency)}</span>
                              {payment.payment_type === "art_director_fee" && (
                                <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs">
                                  AD Fee
                                </Badge>
                              )}
                              {payment.is_advance && (
                                <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20 text-xs">
                                  Advance
                                </Badge>
                              )}
                              {payment.is_overage && (
                                <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Over Budget
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-zinc-500">
                              {new Date(payment.created_at).toLocaleDateString()}
                              {payment.created_by_name && ` • Created by ${payment.created_by_name}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {payment.status === "paid" ? (
                              <div className="text-right">
                                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Paid
                                </Badge>
                                {payment.paid_date && (
                                  <p className="text-xs text-green-400/70 mt-0.5 font-mono">{new Date(payment.paid_date).toLocaleDateString()}</p>
                                )}
                                {payment.paid_by_name && (
                                  <p className="text-xs text-zinc-500 mt-0.5">by {payment.paid_by_name}</p>
                                )}
                              </div>
                            ) : payment.status === "approved" ? (
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs">
                                    Approved
                                  </Badge>
                                  {payment.approved_by_name && (
                                    <p className="text-xs text-zinc-500 mt-0.5">by {payment.approved_by_name}</p>
                                  )}
                                </div>
                                {canMarkPaymentPaid && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleMarkPaymentPaid(payment.id)}
                                    className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                                    data-testid={`pay-task-payment-${idx}`}
                                  >
                                    Mark Paid
                                  </Button>
                                )}
                              </div>
                            ) : payment.status === "confirmed" ? (
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20 text-xs">
                                  Confirmed
                                </Badge>
                                {canApprovePayment && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleApprovePayment(payment.id)}
                                    className="bg-purple-600 hover:bg-purple-700 h-7 text-xs"
                                  >
                                    Approve
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <>
                                <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-xs">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending
                                </Badge>
                                {canApprovePayment && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleConfirmPayment(payment.id)}
                                    className="text-sky-400 hover:bg-sky-500/10 h-7 text-xs"
                                    data-testid={`confirm-task-payment-${idx}`}
                                  >
                                    Confirm
                                  </Button>
                                )}
                              </>
                            )}
                            {canDeletePayment && payment.status !== "paid" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeletePayment(payment.id)}
                                className="text-red-400 hover:bg-red-500/10 h-7 px-2"
                                title="Delete payment request"
                                data-testid={`delete-task-payment-${idx}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {payment.is_overage && payment.overage_reason && (
                          <p className="text-xs text-red-300 mt-1 p-1.5 bg-red-500/5 rounded">
                            Reason: {payment.overage_reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Staff Hours Tracking - for PMs and Art Directors, NOT for partners or clients */}
      {["admin", "pm_lead", "pm", "team", "art_director"].includes(user?.role) && (
        <div className="mt-6">
          <StaffHoursTracker
            taskId={id}
            staffHours={task?.staff_hours || []}
            totalCost={task?.staff_cost_total || 0}
            onUpdate={fetchData}
            canEdit={["admin", "pm_lead", "team", "pm", "art_director"].includes(user?.role)}
            hideCosts={false}
          />
        </div>
      )}

      {/* Who moved the stage and the auto-notes ("status change → in_progress")
          are ours; the client follows the stage on Task Progress. */}
      {!isClientRole && taskTimeline?.stage_history?.length > 0 && (
        <div className="mt-6">
          <TaskStageHistory history={taskTimeline.stage_history} stages={taskTimeline.stages} />
        </div>
      )}

      {/* Image Preview Modal */}
      {previewFile && (
        <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl">
            <DialogHeader>
              <DialogTitle className="text-zinc-50">{previewFile.filename}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <img
                src={getFileUrl(previewFile.id)}
                alt={previewFile.filename}
                className="w-full h-auto rounded-lg"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Edit Description Dialog */}
      <Dialog open={editDescriptionOpen} onOpenChange={setEditDescriptionOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Edit Task Description</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Description</Label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Short description of the task..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 min-h-[80px]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Detailed Brief</Label>
              <Textarea
                value={editBrief}
                onChange={(e) => setEditBrief(e.target.value)}
                placeholder="Detailed brief, references, requirements..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 min-h-[150px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDescriptionOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveDescription}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskDetailPage;
